<template>
  <div class="register-page">
    <div class="register-cont container">
      <div class="row justify-content-center">
        <div class="col-lg-6 col-md-8 d-flex flex-column pt-4 pb-5">
          <img
            class="register-logo mx-auto my-3"
            src="https://cdn.gold549.com/build/assets/img/brand/logo.png"
            alt="GOLD 549"
            @click="$router.push('/home')"
          />

          <div class="register-header text-center">
            <h1 class="register-title">
              {{
                $store.state.language === 'en'
                  ? 'Create your account'
                  : 'အကောင့်အသစ် ဖွင့်မည်'
              }}
            </h1>
            <p class="register-subtitle">
              {{
                $store.state.language === 'en'
                  ? 'Join GOLD 549 in a few steps'
                  : 'အဆင့်အနည်းငယ်ဖြင့် GOLD 549 သို့ ဝင်ရောက်ပါ'
              }}
            </p>
          </div>

          <div
            v-if="referralCode"
            class="referral-banner mx-auto"
            role="status"
          >
            <i class="fas fa-user-friends referral-banner__icon" aria-hidden="true" />
            <div class="referral-banner__text">
              <span class="referral-banner__label">
                {{
                  $store.state.language === 'en'
                    ? 'Referral code applied'
                    : 'ရည်ညွှန်းကုဒ် သုံးထားသည်'
                }}
              </span>
              <strong class="referral-banner__code">{{ referralCode }}</strong>
            </div>
          </div>

          <div class="card border-0 register-card">
            <div class="card-body px-lg-5 py-lg-5">
              <div v-if="error" class="register-error" role="alert">
                {{
                  $store.state.language === 'en'
                    ? 'Registration failed. Please check your details and try again.'
                    : 'အကောင့်ဖွင့်ခြင်း မအောင်မြင်ပါ။ အချက်အလက်များကို ပြန်စစ်ပြီး ကြိုးစားပါ။'
                }}
              </div>

              <form role="form" @submit.prevent="register">
                <div class="form-group mb-3">
                  <label class="field-label" for="register-name">
                    {{ $store.state.language === 'en' ? 'Username' : 'အသုံးပြုသူအမည်' }}
                  </label>
                  <input
                    id="register-name"
                    v-model="name"
                    class="form-control register-input"
                    type="text"
                    autocomplete="username"
                    :placeholder="
                      $store.state.language === 'en' ? 'Choose a username' : 'အမည် ရိုက်ထည့်ပါ'
                    "
                    @keyup.enter="focusRef('phone')"
                  />
                  <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
                </div>

                <div class="form-group mb-3">
                  <label class="field-label" for="register-phone">
                    {{ $store.state.language === 'en' ? 'Phone number' : 'ဖုန်းနံပါတ်' }}
                  </label>
                  <input
                    id="register-phone"
                    ref="phone"
                    v-model="phone"
                    class="form-control register-input"
                    type="tel"
                    autocomplete="tel"
                    placeholder="09xxxxxxxxx"
                    @keyup.enter="focusRef('password')"
                  />
                  <small v-if="errors.phone" class="text-danger">{{ errors.phone }}</small>
                </div>

                <div class="form-group mb-3">
                  <label class="field-label" for="register-password">
                    {{ $store.state.language === 'en' ? 'Password' : 'လျို့ဝှက်နံပါတ်' }}
                  </label>
                  <div class="register-input-wrap">
                    <input
                      id="register-password"
                      ref="password"
                      v-model="password"
                      class="form-control register-input"
                      :type="isShow ? 'text' : 'password'"
                      autocomplete="new-password"
                      :placeholder="
                        $store.state.language === 'en' ? 'Create a password' : 'လျို့ဝှက်နံပါတ်'
                      "
                      @keyup.enter="focusRef('passwordConfirm')"
                    />
                    <i
                      v-if="isShow"
                      class="far fa-eye showable-password"
                      @click="showablePassword"
                    />
                    <i
                      v-else
                      class="fas fa-eye-slash showable-password"
                      @click="showablePassword"
                    />
                  </div>
                  <small v-if="errors.password" class="text-danger">{{ errors.password }}</small>
                </div>

                <div class="form-group mb-3">
                  <label class="field-label" for="register-password-confirm">
                    {{
                      $store.state.language === 'en'
                        ? 'Confirm password'
                        : 'လျို့ဝှက်နံပါတ် အတည်ပြု'
                    }}
                  </label>
                  <input
                    id="register-password-confirm"
                    ref="passwordConfirm"
                    v-model="passwordConfirmation"
                    class="form-control register-input"
                    :type="isShow ? 'text' : 'password'"
                    autocomplete="new-password"
                    :placeholder="
                      $store.state.language === 'en'
                        ? 'Repeat your password'
                        : 'လျို့ဝှက်နံပါတ် ထပ်ရိုက်ထည့်ပါ'
                    "
                    @keyup.enter="register"
                  />
                  <small
                    v-if="errors.password_confirmation"
                    class="text-danger"
                  >{{ errors.password_confirmation }}</small>
                </div>

                <div v-if="referralCode" class="form-group mb-4">
                  <label class="field-label" for="register-referral">
                    {{ $store.state.language === 'en' ? 'Referral code' : 'ရည်ညွှန်းကုဒ်' }}
                  </label>
                  <input
                    id="register-referral"
                    class="form-control register-input register-input--readonly"
                    type="text"
                    :value="referralCode"
                    readonly
                  />
                </div>

                <div class="text-center">
                  <button
                    type="submit"
                    class="btn btn-register my-2"
                    :disabled="isRegistering"
                  >
                    <span v-if="!isRegistering">
                      {{
                        $store.state.language === 'en'
                          ? 'Create account'
                          : 'အကောင့် ဖွင့်မည်'
                      }}
                    </span>
                    <Loading
                      v-else
                      color="#02133e"
                      size="24"
                      class="px-4 py-0"
                    />
                  </button>
                </div>
              </form>
            </div>
          </div>

          <p class="register-footer text-center mt-4">
            {{
              $store.state.language === 'en'
                ? 'Already have an account?'
                : 'အကောင့်ရှိပြီးသားလား?'
            }}
            <a class="register-footer__link" @click.prevent="$router.push('/auth/login')">
              {{
                $store.state.language === 'en' ? 'Sign in' : 'အကောင့် ဝင်မည်'
              }}
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast, Loading } from 'vant';
import { mapActions } from 'vuex';

const REFERRAL_STORAGE_KEY = 'referralCode';

export default {
  components: { Loading },
  data() {
    return {
      name: null,
      phone: null,
      password: null,
      passwordConfirmation: null,
      referralCode: '',
      error: false,
      errors: {},
      isRegistering: false,
      isShow: false,
    };
  },
  watch: {
    $route: {
      handler() {
        this.syncReferralFromRoute();
      },
      immediate: false,
    },
  },
  methods: {
    ...mapActions(['fetchUser']),

    getReferralFromRoute() {
      const { code } = this.$route.params;
      const q = this.$route.query;
      const value =
        code || q.ref || q.referral || q.referral_code || q.code || '';
      return String(value).trim();
    },

    syncReferralFromRoute() {
      const fromRoute = this.getReferralFromRoute();
      if (fromRoute) {
        localStorage.setItem(REFERRAL_STORAGE_KEY, fromRoute);
        this.referralCode = fromRoute;
        return;
      }
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
      this.referralCode = stored ? String(stored).trim() : '';
    },

    getStoredReferralCode() {
      const fromRoute = this.getReferralFromRoute();
      if (fromRoute) {
        localStorage.setItem(REFERRAL_STORAGE_KEY, fromRoute);
        return fromRoute;
      }
      return (
        localStorage.getItem(REFERRAL_STORAGE_KEY) ||
        this.referralCode ||
        ''
      )
        .toString()
        .trim();
    },

    focusRef(refName) {
      const el = this.$refs[refName];
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    },

    showablePassword() {
      this.isShow = !this.isShow;
    },

    validateForm() {
      this.errors = {};
      const isEn = this.$store.state.language === 'en';

      if (!this.name || !String(this.name).trim()) {
        this.errors.name = isEn ? 'Username is required' : 'အမည် လိုအပ်ပါသည်';
      }
      if (!this.phone || !String(this.phone).trim()) {
        this.errors.phone = isEn ? 'Phone number is required' : 'ဖုန်းနံပါတ် လိုအပ်ပါသည်';
      }
      if (!this.password) {
        this.errors.password = isEn ? 'Password is required' : 'လျို့ဝှက်နံပါတ် လိုအပ်ပါသည်';
      } else if (this.password.length < 6) {
        this.errors.password = isEn
          ? 'Password must be at least 6 characters'
          : 'လျို့ဝှက်နံပါတ် အနည်းဆုံး ၆ လုံး';
      }
      if (!this.passwordConfirmation) {
        this.errors.password_confirmation = isEn
          ? 'Please confirm your password'
          : 'လျို့ဝှက်နံပါတ် အတည်ပြုရန် လိုအပ်ပါသည်';
      } else if (this.password !== this.passwordConfirmation) {
        this.errors.password_confirmation = isEn
          ? 'Passwords do not match'
          : 'လျို့ဝှက်နံပါတ် မတူညီပါ';
      }

      return Object.keys(this.errors).length === 0;
    },

    async register() {
      this.error = false;
      if (!this.validateForm()) {
        return;
      }

      const referral_code = this.getStoredReferralCode();

      this.isRegistering = true;
      try {
        const payload = {
          name: String(this.name).trim(),
          phone: String(this.phone).trim(),
          password: this.password,
          password_confirmation: this.passwordConfirmation,
          roles: 'normal',
        };
        if (referral_code) {
          payload.referral_code = referral_code;
        }

        const res = await this.axios.post('/auth/register', payload);

        if (res.data && res.data.token) {
          localStorage.setItem('token', res.data.token);
          const currentTime = new Date();
          localStorage.setItem(
            'tokenExpire',
            new Date(currentTime.getTime() + 60 * 60 * 1000)
          );
          this.axios.defaults.headers.common.Authorization = `Bearer ${res.data.token}`;
          await this.fetchUser();
          Toast.success(
            this.$store.state.language === 'en'
              ? 'Account created successfully!'
              : 'အကောင့် ဖွင့်ပြီးပါပြီ'
          );
          this.$router.push('/home');
          return;
        }

        Toast.success(
          this.$store.state.language === 'en'
            ? 'Registration submitted!'
            : 'အကောင့်ဖွင့်ခြင်း အောင်မြင်ပါသည်'
        );
        this.$router.push('/auth/login');
      } catch (err) {
        this.error = true;
        const serverErrors = err.response && err.response.data && err.response.data.errors;
        if (serverErrors) {
          const mapped = {};
          Object.keys(serverErrors).forEach((key) => {
            const messages = serverErrors[key];
            mapped[key] = Array.isArray(messages) ? messages[0] : messages;
          });
          this.errors = mapped;
        }
      } finally {
        this.isRegistering = false;
      }
    },
  },
  async mounted() {
    this.syncReferralFromRoute();
    const authUser = await this.fetchUser();
    if (authUser && this.$store.state.authUser.roles === 'normal') {
      this.$router.push('/home');
    }
  },
};
</script>

<style scoped>
.register-page {
  min-height: 100vh;
  background: linear-gradient(165deg, #010d28 0%, #02133e 45%, #0a2a5c 100%);
}

.register-cont {
  padding-top: 1.5rem;
  padding-bottom: 2rem;
}

.register-logo {
  width: 120px;
  height: 120px;
  cursor: pointer;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease;
}

.register-logo:hover {
  transform: scale(1.03);
}

.register-header {
  margin-bottom: 1.25rem;
}

.register-title {
  color: #fff;
  font-size: 1.65rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
}

.register-subtitle {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.95rem;
  margin-bottom: 0;
}

.referral-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  max-width: 420px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 12px;
  background: linear-gradient(90deg, rgba(212, 175, 55, 0.2), rgba(212, 175, 55, 0.08));
  border: 1px solid rgba(212, 175, 55, 0.45);
}

.referral-banner__icon {
  color: #d4af37;
  font-size: 1.25rem;
}

.referral-banner__text {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.referral-banner__label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.referral-banner__code {
  color: #f5e6a8;
  font-size: 1.1rem;
  letter-spacing: 0.06em;
}

.register-card {
  background: #f8f9fc;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
}

.field-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 0.4rem;
}

.register-input {
  height: 46px;
  padding: 0.5rem 0.85rem;
  border: 1px solid #d1d9e6;
  border-radius: 10px;
  background: #fff;
  font-size: 1rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.register-input:focus {
  border-color: #02133e;
  box-shadow: 0 0 0 3px rgba(2, 19, 62, 0.12);
  outline: none;
}

.register-input--readonly {
  background: #eef1f8;
  color: #02133e;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.register-input-wrap {
  position: relative;
}

.register-input-wrap .register-input {
  padding-right: 2.5rem;
}

.showable-password {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #8898aa;
  z-index: 2;
}

.btn-register {
  width: 100%;
  max-width: 280px;
  padding: 0.75rem 1.5rem;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  color: #02133e;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 50%, #b8962e 100%);
  box-shadow: 0 4px 14px rgba(212, 175, 55, 0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-register:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(212, 175, 55, 0.55);
  color: #010d28;
}

.btn-register:disabled {
  opacity: 0.85;
  cursor: not-allowed;
}

.register-error {
  margin-bottom: 1rem;
  padding: 0.65rem 1rem;
  color: #c53030;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid rgba(197, 48, 48, 0.35);
  border-radius: 10px;
  background: rgba(197, 48, 48, 0.08);
}

.register-footer {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
}

.register-footer__link {
  color: #e8c547;
  font-weight: 700;
  cursor: pointer;
  margin-left: 0.35rem;
  text-decoration: none;
}

.register-footer__link:hover {
  color: #f5e6a8;
  text-decoration: underline;
}

@media (max-width: 576px) {
  .register-logo {
    width: 100px;
    height: 100px;
  }

  .register-title {
    font-size: 1.35rem;
  }
}
</style>
